/**
 * BatchResultsDialog Component
 *
 * Displays per-location results for a batch operation in a mobile-friendly bottom sheet.
 * Each result row can be expanded to show full log output with copy functionality.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, ExternalLink, ChevronRight, Copy, Check } from 'lucide-react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { BatchOperation, BatchLocationResult } from '@/api/batch-operations';

interface BatchResultsDialogProps {
  operation: BatchOperation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_ICONS: Record<string, typeof CheckCircle> = {
  success: CheckCircle,
  failed: XCircle,
  skipped: AlertTriangle,
};

const STATUS_COLORS: Record<string, string> = {
  success: 'text-green-500',
  failed: 'text-red-500',
  skipped: 'text-yellow-500',
};

const STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  success: 'secondary',
  failed: 'destructive',
  skipped: 'outline',
};

interface ResultRowProps {
  result: BatchLocationResult;
  isExpanded: boolean;
  onToggle: () => void;
}

function ResultRow({ result, isExpanded, onToggle }: ResultRowProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const StatusIcon = STATUS_ICONS[result.status] ?? AlertTriangle;
  const statusColor = STATUS_COLORS[result.status] ?? 'text-muted-foreground';
  const badgeVariant = STATUS_BADGE_VARIANTS[result.status] ?? 'outline';
  const hasMessage = result.message != null && result.message.length > 0;

  const handleCopy = async (): Promise<void> => {
    if (result.message) {
      await navigator.clipboard.writeText(result.message);
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 2000);
    }
  };

  return (
    <div
      className="border-b last:border-b-0"
      data-testid={`batch-result-row-${result.locationId}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!hasMessage}
        className={cn(
          'w-full flex items-center gap-3 p-3 text-left transition-colors',
          hasMessage && 'hover:bg-muted/50 cursor-pointer',
          !hasMessage && 'cursor-default'
        )}
        data-testid={`batch-result-toggle-${result.locationId}`}
      >
        {hasMessage && (
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform shrink-0',
              isExpanded && 'rotate-90'
            )}
          />
        )}
        {!hasMessage && <div className="w-4" />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link
              to={`/synced-locations/${result.locationId}`}
              className="text-primary hover:underline truncate"
              onClick={(e) => { e.stopPropagation(); }}
              data-testid={`batch-result-location-link-${result.locationId}`}
            >
              {result.locationName || `Location #${result.locationId}`}
              <ExternalLink className="inline h-3 w-3 ml-1" />
            </Link>
          </div>
          {!isExpanded && hasMessage && (
            <p className="text-sm text-muted-foreground truncate mt-0.5">
              {result.message}
            </p>
          )}
        </div>

        <Badge variant={badgeVariant} className="flex items-center gap-1 shrink-0">
          <StatusIcon className={`h-3 w-3 ${statusColor}`} />
          {result.status}
        </Badge>
      </button>

      {isExpanded && hasMessage && (
        <div className="px-3 pb-3 pl-10" data-testid={`batch-result-details-${result.locationId}`}>
          <div className="relative">
            <pre className="text-sm bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words font-mono">
              {result.message}
            </pre>
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2 right-2 h-7 w-7 p-0"
              onClick={(e) => {
                e.stopPropagation();
                void handleCopy();
              }}
              data-testid={`batch-result-copy-${result.locationId}`}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BatchResultsDialog({
  operation,
  open,
  onOpenChange,
}: BatchResultsDialogProps): JSX.Element {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const results = operation.results ?? [];

  // Group results by status for summary
  const successCount = results.filter((r) => r.status === 'success').length;
  const failedCount = results.filter((r) => r.status === 'failed').length;
  const skippedCount = results.filter((r) => r.status === 'skipped').length;

  const toggleExpanded = (locationId: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(locationId)) {
        next.delete(locationId);
      } else {
        next.add(locationId);
      }
      return next;
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]" data-testid="batch-results-dialog">
        <DrawerHeader>
          <DrawerTitle>Batch Operation Results</DrawerTitle>
          <DrawerDescription>
            Processed {String(results.length)} locations:
            <span className="ml-2">
              <span className="text-green-600">{String(successCount)} success</span>
              {' | '}
              <span className="text-red-600">{String(failedCount)} failed</span>
              {' | '}
              <span className="text-yellow-600">{String(skippedCount)} skipped</span>
            </span>
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex-1 overflow-auto px-4 pb-4">
          {results.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No results yet
            </div>
          ) : (
            <div className="border rounded-md">
              {results.map((result) => (
                <ResultRow
                  key={result.locationId}
                  result={result}
                  isExpanded={expandedIds.has(result.locationId)}
                  onToggle={() => { toggleExpanded(result.locationId); }}
                />
              ))}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
